import { Injectable } from '@angular/core';
import {
  AdobeAnalytics,
  AnalyticsPageNames,
  AnalyticsSectionNames,
  CacheConstant
} from '../../constants/app.constant';
import { Router } from '@angular/router';
import {
  UserRoutes,
  AEMRoutes,
  RoutePrefix,
  ContactUsRoutes,
  TrackAndTraceRoutes,
  OrderRoutes,
  ProductRoutes,
  OktaRoutes
} from '../../constants/routes.constant';
import { AnalyticsService } from '../analytics.service';
import { DatePipe } from '@angular/common';
import { AuthService } from '../auth.service';
import { AppUtilService } from '../../utils/app-util.service';
import { BaseComponent } from '../../../app.base.component';
import { DeviceDetectorService } from 'ngx-device-detector';
import { BehaviorSubject } from 'rxjs';
import { AppCacheService } from '../cache/app-cache.service';
import { AppConfigService } from '../init/app-config.service';

declare var _satellite: any;
declare var pageData: any;
declare var window: any;

@Injectable()
export class AppRouteHandlerService extends BaseComponent {
  private analyticsData = {
    pageName: '',
    pageSection: ''
  };
  
  isAppDown = new BehaviorSubject<boolean>(false);
  /*********************Properties*********************/

  /*********************Constructor*********************/
  constructor(
    private authService: AuthService,
    private router: Router,
    private analyticsService: AnalyticsService,
    private datepipe: DatePipe,
    deviceDetectorService: DeviceDetectorService,
    private cache: AppCacheService,
    private appConfig: AppConfigService
  ) {
    super(deviceDetectorService, cache, appConfig);
    this.router.events.subscribe(event => {
      this.onRouterEventChange(event);
    });
  }
  /*********************Constructor*********************/

  /*********************Private Methods*********************/

  private onRouterEventChange(event): any {
    if (!this.isMobileDevice()) {
      const isAuthenticated = this.authService.isAuthenticated();

      if (event.toString().startsWith('NavigationStart')) {
        if (event.url === '/' || event.url === window.origin) {
          this.router.navigate([
            isAuthenticated ? RoutePrefix.MyHome : RoutePrefix.Home
          ]);

          return;
        }

        if (event.url === '/' + RoutePrefix.Home && isAuthenticated) {
          this.router.navigate([RoutePrefix.MyHome]);
          return;
        }

        if (event.url === '/' + RoutePrefix.MyHome && !isAuthenticated) {
          this.router.navigate([RoutePrefix.Home]);
          return;
        }
      }

      if (event.toString().startsWith('NavigationError')) {
        AppUtilService.redirectToHome();

        return;
      }
    }

    if (event.toString().startsWith('NavigationEnd')) {
      // Added typeof check for _satelite variable due to external dependency loaded dynamically
      // Which is causing error on application load for first navigation.
      if (typeof _satellite !== 'undefined') {
        // commented to use metainfo api
        // this.analyticsService.getServerDate().subscribe((res: any) => {
        this.analyticsService.getMetaInfo().subscribe((res: any) => {
          if (!res.hasError) {
            let appVersion = -1;
            appVersion = AppUtilService.cache.get(CacheConstant.App_Version);
            AppUtilService.cache.set(CacheConstant.App_Version, res.appVersion);
            if (appVersion && appVersion !== res.appVersion) {
              AppUtilService.cache.set(CacheConstant.App_Version, res.appVersion);
              this.forceRefresh();
            }
            this.setPageData(event);
            const currentDate = new Date(res.estDateTime);
            const dateString = this.datepipe.transform(
              currentDate,
              'MM/dd/yyyy'
            );
            pageData.page.name = this.analyticsData.pageName;
            pageData.page.section = this.analyticsData.pageSection;
            pageData.page.serverDate = dateString;
            pageData.site.name = 'parmed';
            pageData.site.environment = this.cache.get(CacheConstant.Env_Properties).environmentName;
            _satellite.track(AdobeAnalytics.Condition_String);

            this.isAppDown.next(res.appDown);
          }
        });
      }
    }
  }

  private setPageData(event) {
    let url = event.url;
    let pageName: string;
    let pageSection: string;
    if (url.charAt(0) === '/') {
      url = url.substr(1);
    }
    switch (url) {
      case UserRoutes.ChangeAccount:
        pageName = AnalyticsPageNames.CHANGE_ACCOUNT;
        pageSection = AnalyticsSectionNames.ACCOUNT_MAINTENANCE;
        break;
      case AEMRoutes.MyAccount:
        pageName = AnalyticsPageNames.MY_ACCOUNT;
        pageSection = AnalyticsSectionNames.ACCOUNT_MAINTENANCE;
        break;
      case AEMRoutes.MyAccountHarvard:
        pageName = AnalyticsPageNames.MY_ACCOUNT;
        pageSection = AnalyticsSectionNames.ACCOUNT_MAINTENANCE;
        break;
      case RoutePrefix.ContactUs:
        pageName = AnalyticsPageNames.CONTACT_US;
        pageSection = AnalyticsSectionNames.CONTECT_US;
        break;
      case ContactUsRoutes.ContactUsThankYou:
        pageName = AnalyticsPageNames.CONTACT_US_THANKYOU;
        pageSection = AnalyticsSectionNames.CONTECT_US;
        break;
      case AEMRoutes.Help:
        pageName = AnalyticsPageNames.CSOS_HELP;
        pageSection = AnalyticsSectionNames.CSOSHELP;
        break;
      case 'DSCSA':
        pageName = AnalyticsPageNames.DSCSA;
        pageSection = AnalyticsSectionNames.DSCSA;
        break;
      case TrackAndTraceRoutes.DscsaTransactionData:
        pageName = AnalyticsPageNames.DSCSA_TR_DATA;
        pageSection = AnalyticsSectionNames.DSCSA;
        break;
      case TrackAndTraceRoutes.SearchTransactionData:
        pageName = AnalyticsPageNames.DSCSA_SEARCH_DATA;
        pageSection = AnalyticsSectionNames.DSCSA;
        break;
      case OrderRoutes.OrderHistory:
        pageName = AnalyticsPageNames.ORDER_HISTORY;
        pageSection = AnalyticsSectionNames.DSCSA;
        break;
      case OrderRoutes.CsosOrderHistory:
        pageName = AnalyticsPageNames.CSOS_ORDER_HISTORY;
        pageSection = AnalyticsSectionNames.HISTORICAL_DATA;
        break;
      case ProductRoutes.ProductHistory:
        pageName = AnalyticsPageNames.PRODUCT_HISTORY;
        pageSection = AnalyticsSectionNames.HISTORICAL_DATA;
        break;
      case 'SecureHome':
        if (this.authService.isAuthenticated()) {
          pageName = AnalyticsPageNames.MY_HOME;
        } else {
          pageName = AnalyticsPageNames.HOME;
        }
        pageSection = AnalyticsSectionNames.HOME;
        break;
      case 'maintenance':
        pageName = AnalyticsPageNames.MAINTENANCE;
        pageSection = AnalyticsSectionNames.MAINTENANCE;
        break;
      case OktaRoutes.ActivateUser:
        pageName = AnalyticsPageNames.ACTIVATE_ACCOUNT;
        pageSection = AnalyticsSectionNames.NEW_CUSTOMER;
        break;
      case 'customer forms':
        pageName = AnalyticsPageNames.CUSTOMER_FORMS;
        pageSection = AnalyticsSectionNames.NEW_CUSTOMER;
        break;
      case RoutePrefix.Register:
        pageName = AnalyticsPageNames.REGISTER_ONLINE;
        pageSection = AnalyticsSectionNames.NEW_CUSTOMER;
        break;
      case RoutePrefix.CreditCards:
        pageName = AnalyticsPageNames.CARD_MGT;
        pageSection = AnalyticsSectionNames.PAYMENTS;
        break;
      case 'CreditCard Payment':
        pageName = AnalyticsPageNames.CREDIT_CARD_PAYMENT;
        pageSection = AnalyticsSectionNames.PAYMENTS;
        break;
      case OrderRoutes.PaymentOptions:
        pageName = AnalyticsPageNames.PAYMENT_OPTIONS;
        pageSection = AnalyticsSectionNames.PAYMENTS;
        break;
      case AEMRoutes.AboutUs:
        pageName = AnalyticsPageNames.ABOUT_US;
        pageSection = AnalyticsSectionNames.PUBLIC;
        break;
      case AEMRoutes.Employment:
        pageName = AnalyticsPageNames.EMPLOYMENT;
        pageSection = AnalyticsSectionNames.PUBLIC;
        break;
      // case AEMRoutes.Help:
      //   pageName = AnalyticsPageNames.HELP;
      //   pageSection = AnalyticsSectionNames.PUBLIC;
      //   break;
      case RoutePrefix.Home:
        pageName = AnalyticsPageNames.HOME;
        pageSection = AnalyticsSectionNames.PUBLIC;
        break;
      case AEMRoutes.Legal:
        pageName = AnalyticsPageNames.LEGAL;
        pageSection = AnalyticsSectionNames.PUBLIC;
        break;
      case AEMRoutes.Privacy:
        pageName = AnalyticsPageNames.PRIVACY;
        pageSection = AnalyticsSectionNames.PUBLIC;
        break;
      case AEMRoutes.ProductServices:
        pageName = AnalyticsPageNames.PRODUCT_SERVICES;
        pageSection = AnalyticsSectionNames.PUBLIC;
        break;
      case 'return confirmation':
        pageName = AnalyticsPageNames.RETURN_CONFIRMATION;
        pageSection = AnalyticsSectionNames.RETURN_CONFIRMATION;
        break;
      case 'request return':
        pageName = AnalyticsPageNames.REQUEST_RETURN;
        pageSection = AnalyticsSectionNames.RETURNS;
        break;
      case 'return product details':
        pageName = AnalyticsPageNames.RETURNS_PRODUCT_DETAILS;
        pageSection = AnalyticsSectionNames.RETURNS;
        break;
      case 'return store location':
        pageName = AnalyticsPageNames.RETURNS_STORE_LOCATIONS;
        pageSection = AnalyticsSectionNames.RETURNS;
        break;
      case 'SDS':
        pageName = AnalyticsPageNames.SDS;
        pageSection = AnalyticsSectionNames.SDS;
        break;
      case 'emedbinaxNOW':
        pageName = AnalyticsPageNames.EMED_BINAX_NOW;
        pageSection = AnalyticsSectionNames.EMED_BINAX_NOW;
        break;
      case 'Antibiotics':
        pageName = AnalyticsPageNames.ANTIBIOTICS;
        pageSection = AnalyticsSectionNames.SHOP;
        break;
      case 'frequently dispensed products':
        pageName = AnalyticsPageNames.FREQUENTLY_DISPENSED;
        pageSection = AnalyticsSectionNames.SHOP;
        break;
      case 'full catalogs':
        pageName = AnalyticsPageNames.FULL_CATALOGS;
        pageSection = AnalyticsSectionNames.SHOP;
        break;
      case 'Oral Contracptives':
        pageName = AnalyticsPageNames.ORAL;
        pageSection = AnalyticsSectionNames.SHOP;
        break;
      case OrderRoutes.OrderSummary:
        pageName = AnalyticsPageNames.ORDER_SUMMARY;
        pageSection = AnalyticsSectionNames.SHOP;
        break;
      case 'OTC products':
        pageName = AnalyticsPageNames.OTC_PRODUCTS;
        pageSection = AnalyticsSectionNames.SHOP;
        break;
      case 'Prebook':
        pageName = AnalyticsPageNames.PREBOOK;
        pageSection = AnalyticsSectionNames.SHOP;
        break;
      case ProductRoutes.Detail:
        pageName = AnalyticsPageNames.PRODUCT_DETAILS;
        pageSection = AnalyticsSectionNames.SHOP;
        break;
      case OrderRoutes.QuickOrder:
        pageName = AnalyticsPageNames.QUICK_ORDER;
        pageSection = AnalyticsSectionNames.SHOP;
        break;
      case 'recently launched generics':
        pageName = '';
        pageSection = AnalyticsSectionNames.SHOP;
        break;
      case ProductRoutes.BasicSearch:
        pageName = AnalyticsPageNames.SEARCH_RESULTS;
        pageSection = AnalyticsSectionNames.SHOP;
        break;
      case ProductRoutes.AdvanceSearch:
        pageName = AnalyticsPageNames.SEARCH_RESULTS;
        pageSection = AnalyticsSectionNames.SHOP;
        break;
      case AEMRoutes.Shop:
        pageName = AnalyticsPageNames.SHOP;
        pageSection = AnalyticsSectionNames.SHOP;
        break;
      case OrderRoutes.ShoppingCart:
        pageName = AnalyticsPageNames.CART;
        pageSection = AnalyticsSectionNames.SHOP;
        break;
      case 'Specials':
        pageName = AnalyticsPageNames.SPECIALS;
        pageSection = AnalyticsSectionNames.SHOP;
        break;
      default:
        pageName = '';
        pageSection = '';
        break;
    }
    this.analyticsData.pageName = pageName;
    this.analyticsData.pageSection = pageSection;
  }

  private forceRefresh(): void {
    window.location.reload(true);
  }
  /*********************Private Methods*********************/
}
